import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import papi, { formatErr } from "@/lib/partnerApi";
import PartnerNav from "@/pages/partner/PartnerNav";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Users, FolderKanban, FileCheck2, MessageSquare, Bot, MapPin, Search } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtBudget = (n) => (n ? "₹" + Number(n).toLocaleString("en-IN") : "—");

export default function AdminCustomers() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("customers");
  const [q, setQ] = useState("");
  const role = localStorage.getItem("partner_role");

  useEffect(() => {
    if (!["manager", "admin"].includes(role)) { navigate("/login"); return; }
    papi.get("/partner/manage/customers").then((r) => setData(r.data))
      .catch((e) => { if ([401, 403].includes(e.response?.status)) navigate("/login"); else toast.error(formatErr(e.response?.data?.detail)); });
  }, []);

  const stats = data ? [
    { icon: Users, label: "Customers Signed Up", value: data.totals.customers, accent: "teal" },
    { icon: FolderKanban, label: "Projects Created", value: data.totals.projects, accent: "sky" },
    { icon: FileCheck2, label: "Health Reports Generated", value: data.totals.projects_with_report, accent: "emerald" },
    { icon: MessageSquare, label: "Website AI Chats", value: data.totals.website_ai_chats, accent: "violet" },
    { icon: Bot, label: "In-Project AI Chats", value: data.totals.project_ai_chat_users, accent: "amber" },
  ] : [];
  const accents = { teal: "bg-[#0E9AC8]/10 text-[#0E9AC8]", sky: "bg-sky-500/10 text-sky-600", emerald: "bg-emerald-500/10 text-emerald-600", violet: "bg-violet-500/10 text-violet-600", amber: "bg-amber-500/10 text-amber-600" };

  const cityData = data ? Object.entries(data.by_city).map(([name, value]) => ({ name, value })) : [];
  const customers = (data?.customers || []).filter((c) => !q || (c.name + c.email).toLowerCase().includes(q.toLowerCase()));
  const projects = (data?.projects || []).filter((p) => !q || (`${p.name} ${p.city} ${p.owner_name} ${p.owner_email}`).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-[#FBFAF9]">
      <PartnerNav />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-extrabold text-slate-900">Customers & Activity</h1>
          <p className="text-sm text-slate-500">Signups, projects created, AI chats and city-wise project distribution.</p>
        </div>

        {!data ? <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-[#0E9AC8]" /></div> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
              {stats.map((s) => (
                <div key={s.label} data-testid={`cust-stat-${s.label}`} className="card-premium rounded-2xl p-5">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-3 ${accents[s.accent]}`}><s.icon className="h-5 w-5" strokeWidth={1.75} /></div>
                  <p className="font-display text-3xl font-extrabold text-slate-900 tabular-nums">{s.value}</p>
                  <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="card-premium rounded-2xl p-6 mb-6">
              <h3 className="font-display text-base font-bold text-slate-900 mb-4 flex items-center gap-2"><MapPin className="h-4 w-4 text-[#0E9AC8]" /> Projects by City</h3>
              {cityData.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">No projects yet.</p> : (
                <ResponsiveContainer width="100%" height={Math.max(180, cityData.length * 42)}>
                  <BarChart data={cityData} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
                    <Tooltip /><Bar dataKey="value" fill="#0E9AC8" radius={[0, 6, 6, 0]} barSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex gap-2">
                <button data-testid="tab-customers" onClick={() => setTab("customers")} className={`rounded-xl px-4 py-2 text-sm font-semibold ${tab === "customers" ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>Customers ({data.totals.customers})</button>
                <button data-testid="tab-projects" onClick={() => setTab("projects")} className={`rounded-xl px-4 py-2 text-sm font-semibold ${tab === "projects" ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>Projects ({data.totals.projects})</button>
              </div>
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input data-testid="cust-search" className="rounded-xl pl-9" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
            </div>

            <div className="card-premium rounded-2xl overflow-hidden">
              {tab === "customers" ? (
                customers.length === 0 ? <div className="p-14 text-center text-slate-400">No customers found.</div> : (
                  <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="customers-table">
                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
                      <th className="text-left p-3">Customer</th><th className="text-left p-3">Email</th><th className="text-left p-3">Signed Up</th><th className="text-center p-3">Projects</th><th className="text-left p-3">Cities</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {customers.map((c) => (
                        <tr key={c.user_id} className="hover:bg-slate-50">
                          <td className="p-3 font-medium text-slate-800">{c.name}</td>
                          <td className="p-3 text-slate-600">{c.email}</td>
                          <td className="p-3 text-slate-500">{fmtDate(c.created_at)}</td>
                          <td className="p-3 text-center"><span className="inline-flex items-center justify-center h-6 min-w-6 px-2 rounded-full bg-[#0E9AC8]/10 text-[#0E9AC8] font-semibold text-xs">{c.project_count}</span></td>
                          <td className="p-3 text-slate-500">{c.cities.length ? c.cities.join(", ") : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                )
              ) : (
                projects.length === 0 ? <div className="p-14 text-center text-slate-400">No projects found.</div> : (
                  <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="projects-table">
                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
                      <th className="text-left p-3">Project</th><th className="text-left p-3">City</th><th className="text-left p-3">Type</th><th className="text-left p-3">Budget</th><th className="text-center p-3">Score</th><th className="text-left p-3">Owner</th><th className="text-left p-3">Created</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {projects.map((p) => (
                        <tr key={p.id} className="hover:bg-slate-50">
                          <td className="p-3 font-medium text-slate-800">{p.name}</td>
                          <td className="p-3 text-slate-600"><span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-[#0E9AC8]" />{p.city}</span></td>
                          <td className="p-3 text-slate-500 capitalize">{p.construction_type || "—"}</td>
                          <td className="p-3 text-slate-600 tabular-nums">{fmtBudget(p.budget)}</td>
                          <td className="p-3 text-center">{p.score != null ? <span className="font-bold text-slate-700">{p.score}</span> : <span className="text-slate-300">—</span>}</td>
                          <td className="p-3 text-slate-600">{p.owner_name}<div className="text-xs text-slate-400">{p.owner_email}</div></td>
                          <td className="p-3 text-slate-500 whitespace-nowrap">{fmtDate(p.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                )
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
